import type { AxiosError, AxiosInstance } from "axios";
import wx from "weixin-js-sdk";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "~/enum/cache/cookie";
import { PLATFORM_SOURCE_REGISTER } from "~/enum/cache/sessionStorage";
import { API_URL, DATA_URL, LOGIN_HOST } from "~/env";
import { APPID } from "~/env";
import { interceptor, sendAxios, sendRequest, sendSSERequest } from "~/utils/axios-utils";
import { axiosConfig } from "~/utils/axios-utils";
import Cookie from "~/utils/cookie";
import DeviceUtils from "~/utils/device-utils";
import aiApi from "./ai";
import dataApi from "./data";
import dlsApi from "./dls";
import loginApi from "./login";
import siqWebApi from "./siq-web";
import vipApi from "./vip";

export const toLogin = () => {
  if (process.server) return;

  const source = sessionStorage.getItem(PLATFORM_SOURCE_REGISTER) || "";
  let platform = "";
  if (source) {
    platform = `&platform=${source}`;
  }

  let loginPath = "/login";
  if (location.pathname.includes("/game/")) {
    loginPath = "/game/login";
  }
  if (location.href.includes("secretKey")) {
    loginPath = "/h5/siq/home";
  }
  if (DeviceUtils.isWeixin() && location.href.includes("h5/ft")) {
    // 小程序返回上一步
    wx.miniProgram.navigateBack({ delta: 1 } as any);
  }

  const url = new URL(loginPath || "/login", location.origin);
  url.searchParams.set("redirect", location.href);
  location.href = url.href + platform;
};

const onError = (error: AxiosError) => {
  const response = error.response;
  const status = response?.status;
  // Need login
  if (status === 401 || status === 417 || status === 406) {
    Cookie.remove(ACCESS_TOKEN);
    Cookie.remove(REFRESH_TOKEN);
    toLogin();
  }
};

export default ($axios: AxiosInstance) => {
  $axios.defaults.baseURL = API_URL;

  // 创建各个模块的 API 列表
  // 每个模块内部会设置自己的 baseURL
  const dataApiList = dataApi(axiosConfig($axios.create(), { onError }));
  const dlsApiList = dlsApi(axiosConfig($axios.create(), { onError }));
  const aiApiList = aiApi(axiosConfig($axios.create(), { onError }));
  const vipApiList = vipApi(axiosConfig($axios.create(), { onError }));
  const siqWebApiList = siqWebApi(axiosConfig($axios.create(), { onError }));
  const loginApiList = loginApi(axiosConfig($axios.create(), { onError }));

  return {
    ...dataApiList,
    ...dlsApiList,
    ai: aiApiList,
    ...vipApiList,
    ...siqWebApiList,
    ...loginApiList,
    /**
     * 页面访问埋点
     */
    site: () => $axios.get<any, RequestResponse>("/api/v1/web/site"),
    /**
     * 获取当前登录用户信息
     */
    getUserInfo: (type = "") =>
      sendAxios($axios, { method: "GET", url: type === "ai" ? "/ai/api/v1/user/info" : "/api/v1/user/info" }),
    getUserInfoData: () => sendRequest($axios, { method: "GET", url: "/api/v1/user/info" }),
    /**
     * 验证用户是否登录
     */
    auth: () => sendAxios($axios, { method: "GET", url: "/api/v1/user/info" }),
    /**
     * 切换组织
     */
    changeCorp: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/api/v2/user/changeCorp",
        data: params,
      }),
    /**
     * 获取反馈类型
     */
    getFeedbackTypes: () => sendRequest($axios, { method: "GET", url: "/api/v1/feedback/types" }),
    /**
     * 提交反馈
     */
    feedbackAdd: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/api/v1/feedback/add",
        data: params,
      }),
    /**
     * 文件上传
     */
    fileUpload: (form: FormData) => interceptor($axios.post<any, RequestResponse>("/api/file/fileUpload", form)),

    /**
     * 查询资讯列表
     */
    newsQuery: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/api/v1/news/query",
        data: params,
      }),

    /**
     * 查询资讯列表
     */
    newsHomePageQuery: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/api/v1/news/homeNews",
        data: params,
      }),
    /**
     * 查询资讯列表
     */
    newsQueryType: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: `/api/v1/news/classification/${params}`,
      }),
    /**
     * 查询资讯详情
     * TODO: orlando标注：这儿有个type参数，不知道为啥没使用
     * @param _type 类型参数（未使用）
     * @param id 资讯ID
     */
    newsView: (_type: any, id: any) =>
      sendRequest($axios, {
        method: "GET",
        url: "/api/v1/news/view",
        params: { id },
      }),
    /**
     * 查询资讯详情
     */
    newsDetail: (type: any, id: any) =>
      sendRequest($axios, {
        method: "GET",
        url: `/api/v1/news/detail/${type}/${id}`,
      }),
    /**
     * 查询h5活动页详情
     */
    getH5ActivityDetail: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: "/api/v1/promotion/queryH5ContentDetail",
        params,
      }),
    /**
     * refreshToken 刷新token
     */
    refreshTokenFn: (params: any) =>
      sendRequest($axios, {
        headers: {
          "Access-Appid": APPID,
        },
        method: "GET",
        url: `${LOGIN_HOST}/api/v1/accounts/refresh_token`,
        params,
      }),

    /**
     * 获取深i企律所列表
     */
    getLawFirmList: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/api/v1/siqPlatform/lawFirmList",
        data: params,
      }),
    /**
     * 深i企律所详情
     */
    getLawFirmDetail: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: "/api/v1/siqPlatform/lawFirmDetail",
        params,
      }),

    /**
     * 法搜案例
     */
    getNewsQueryList: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: `${DATA_URL}/api/v1/case/queryList`,
        data: params,
      }),

    /**
     * 法搜法规
     */
    getRegulationsQueryList: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: `${DATA_URL}/api/v2/laws/queryList`,
        data: params,
      }),

    /**
     * 控制h5机构板页面显隐
     */
    getH5Setting: () =>
      sendRequest($axios, {
        method: "GET",
        url: "/api/v1/user/setting",
      }),
    getWXConfig: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: "/api/v1/wecaht/getWXConfig",
        params,
      }),

    // 福田掌中宝开始--
    getQuoteLawName: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: `/ai/api/v2/subscribe/quoteLawName`,
        params,
      }),
    getGptChatHistory: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: `/ai/api/v1/gptLawyer/selectGptChatHistoryV2`,
        data: params,
      }),
    getSessionList: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: `/ai/api/v1/gptLawyer/selectSessionList`,
        params,
      }),
    getAgentCount: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: `/ai/api/v1/card/query_agent_count`,
        params,
      }),
    getCommentPageList: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: `/ai/api/v1/card/commentPageList`,
        params,
      }),
    getSubmitComment: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: `/ai/api/v1/card/submitComment`,
        data: params,
      }),
    /**
     * 获取名片信息
     */
    getCardInfo: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: "/ai/api/v1/card/query_card_info_v2",
        params,
      }),
    /**
     * ai律所润色
     */
    aiLawFirmPolish: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/ai/api/v1/card/aiLawFirmPolish",
        data: params,
      }),
    /**
     * 个人简介润色
     */
    aiPersonalProfilePolish: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/ai/api/v1/card/aiPersonalProfilePolish",
        data: params,
      }),
    /**
     * 名片绑定知识库
     */
    bindCardKnowledge: (params: API.BindCardKnowledgeParams) =>
      sendRequest($axios, {
        method: "POST",
        url: "/ai/api/v1/card/bindCardKnowledge",
        data: params,
      }),
    /**
     * 校验知识库是否正确
     */
    checkCardKnowledge: (params: API.CheckCardKnowledgeParams) =>
      sendRequest($axios, {
        method: "POST",
        url: "/ai/api/v1/card/checkCardKnowledge",
        data: params,
      }),
    /**
     * 名片总结
     */
    userSummary: (params: any, config?: FetchEventSourceConfig) =>
      sendSSERequest("/ai/api/v1/card/userSummary", "POST", params, config),

    /**
     * ai搜索润色
     */
    aiCardPolishSearch: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/ai/api/v1/card/aiCardPolishSearchV2",
        data: params,
      }),
    /**
     * 文件保存
     */
    saveFile: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/ai/api/file/saveFile",
        data: params,
      }),
    /**
     * 保存名片
     */
    saveCard: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/ai/api/v1/card/save_card_v2",
        data: params,
      }),
    /**
     * 获取智能体数据
     */
    queryAgentCount: () =>
      sendRequest($axios, {
        method: "GET",
        url: "/ai/api/v1/card/query_agent_count",
      }),
    /**
     * 获取地区数据
     */
    getAreaList: () =>
      sendRequest($axios, {
        method: "GET",
        url: "/ai/api/v1/card/cityList",
      }),
    /**
     * 根据姓名获取名片列表
     */
    queryLawyerName: (params: any) =>
      sendRequest($axios, {
        method: "GET",
        url: "/ai/api/v1/card/queryLawyerName",
        params,
      }),
    /**
     * 智能生成头像
     */
    aiGenerateImg: (params: any) =>
      sendRequest($axios, {
        method: "POST",
        url: "/ai/api/v1/card/aiGenerateImg",
        params,
      }),
    /**
     * 获取分类
     */
    getClassifyListByCardId: (params: API.GetClassifyListByCardIdParams) =>
      sendRequest<API.GetClassifyListByCardIdResult[]>($axios, {
        method: "GET",
        url: "/ai/api/v1/card/classifyListByAppKey",
        params,
      }),
    // 福田掌中宝结束--
  };
};
